import { ReactNode } from 'react'
import { useQuery } from 'react-query'
import {
  FinanceCashSalesReconciliation,
  FinanceReportAnalysis,
  FinanceReportContents,
  FinanceReportDeliveries,
} from 'BoostXpressAdmin/Model/cashReconciliationXModel'
import { stationOperationsReportService } from 'BoostXpressAdmin/Services/stationOperationsReportService'
import { formatMinorCurrency } from 'BoostXpressAdmin/Utils/cashReconciliationMoney'
import {
  formatStationNumber,
  formatStationLitres,
  stationOperationsError,
} from 'BoostXpressAdmin/Utils/stationOperations'

type Loadable<T> =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; data: T }

const numberFormatter = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 2 })
const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)
const formatNumber = formatStationNumber
const formatLitres = formatStationLitres
const formatRounded = (value: number | null | undefined, fallback = 'Not available') =>
  isNumber(value) ? numberFormatter.format(value) : fallback
const formatRoundedLitres = (value: number | null | undefined, fallback = 'Not available') =>
  isNumber(value) ? `${numberFormatter.format(value)} L` : fallback
const formatMoney = (minor: number | null | undefined, fallback = 'Not provided') =>
  isNumber(minor) ? formatMinorCurrency(minor) : fallback
const formatText = (value: string | null | undefined) =>
  typeof value === 'string' && value.trim() ? value : 'Not provided'
const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('en-NG', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Africa/Lagos',
  }).format(new Date(value))
function list<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : []
}

const READING_LABELS: Record<string, string> = {
  OPENING: 'Opening',
  CLOSING: 'Closing',
  PRE_PRICE_CHANGE: 'Before price change',
  POST_PRICE_CHANGE: 'After price change',
  PRE_METER_RESET: 'Before meter reset',
  POST_METER_RESET: 'After meter reset',
}
const readingLabel = (type: string, seq: number) => {
  const label = READING_LABELS[type] ?? String(type).replace(/_/g, ' ')
  return type !== 'OPENING' && type !== 'CLOSING' && isNumber(seq) && seq > 0
    ? `${label} ${seq}`
    : label
}
const readingRank = (type: string) => (type === 'OPENING' ? 0 : type === 'CLOSING' ? 2 : 1)
const pairRank = (type: string) => (type.startsWith('POST_') ? 1 : 0)
const DELIVERY_STATUS_LABELS: Record<string, string> = { LOGGED: 'Logged', VERIFIED: 'Verified' }

const loadContents = async (id: string): Promise<FinanceReportContents> => {
  const data = (await stationOperationsReportService.getContents(id)).data.data
  return {
    pumpReadings: list(data?.pumpReadings),
    tankDips: list(data?.tankDips),
    returnToTank: list(data?.returnToTank),
    cashItems: list(data?.cashItems),
    productPrices: list(data?.productPrices),
    priceChanges: list(data?.priceChanges),
  }
}
const loadAnalysis = async (id: string): Promise<FinanceReportAnalysis> => {
  const data = (await stationOperationsReportService.getAnalysis(id)).data.data
  return { tolerance: list(data?.tolerance), sales: list(data?.sales) }
}
const loadDeliveries = async (id: string): Promise<FinanceReportDeliveries> => {
  const data = (await stationOperationsReportService.getDeliveries(id)).data.data
  return {
    deliveries: list(data?.deliveries),
    tradegridDeliveredLitres: data?.tradegridDeliveredLitres ?? null,
  }
}
const loadCashSalesReconciliation = async (
  id: string,
): Promise<FinanceCashSalesReconciliation> =>
  (await stationOperationsReportService.getCashSalesReconciliation(id)).data.data
function useLoadable<T>(
  id: string,
  load: (id: string) => Promise<T>,
  failureMessage: string,
): [Loadable<T>, () => void] {
  const query = useQuery(['station-operations', 'report-section', load.name, id], () => load(id), {
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: true,
  })
  const retry = () => {
    void query.refetch()
  }
  if (query.isError)
    return [
      { state: 'error', message: stationOperationsError(query.error) || failureMessage },
      retry,
    ]
  if (query.data === undefined) return [{ state: 'loading' }, retry]
  return [{ state: 'ready', data: query.data }, retry]
}
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className='bx-drd-section'>
    <h3 className='bx-drd-section-title'>{title}</h3>
    <div className='bx-drd-card'>{children}</div>
  </section>
)
const SubTitle = ({ children, first = false }: { children: ReactNode; first?: boolean }) => (
  <h4 className={`bx-drd-subtitle${first ? ' bx-drd-subtitle--first' : ''}`}>{children}</h4>
)
const Table = ({
  headers,
  rows,
  emptyMessage,
}: {
  headers: string[]
  rows: { key: string; cells: ReactNode[] }[]
  emptyMessage: string
}) =>
  rows.length === 0 ? (
    <p className='bx-drd-empty-sub'>{emptyMessage}</p>
  ) : (
    <div className='bx-drd-table-responsive'>
      <table className='bx-drd-table'>
        <thead>
          <tr>
            {headers.map((header) => (
              <th scope='col' key={header}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, index) => (
                <td key={headers[index]}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
function renderLoadable<T>(value: Loadable<T>, retry: () => void, render: (data: T) => ReactNode) {
  if (value.state === 'loading')
    return (
      <p className='bx-drd-empty-sub' role='status'>
        Loading report section...
      </p>
    )
  if (value.state === 'error')
    return (
      <div className='bx-drd-section-error' role='alert'>
        <span>{value.message}</span>
        <button type='button' onClick={retry}>
          Try again
        </button>
      </div>
    )
  return render(value.data)
}
interface DailyReportContentsSectionsProps {
  reportResourceId: string
}

const DailyReportContentsSections = ({ reportResourceId }: DailyReportContentsSectionsProps) => {
  const [contents, retryContents] = useLoadable(
    reportResourceId,
    loadContents,
    'The report entries could not be loaded.',
  )
  const [analysis, retryAnalysis] = useLoadable(
    reportResourceId,
    loadAnalysis,
    'The volume analysis could not be loaded.',
  )
  const [deliveries, retryDeliveries] = useLoadable(
    reportResourceId,
    loadDeliveries,
    'The deliveries could not be loaded.',
  )
  const [cashSales, retryCashSales] = useLoadable(
    reportResourceId,
    loadCashSalesReconciliation,
    'The cash-sales reconciliation could not be loaded.',
  )

  const renderContents = (data: FinanceReportContents) => {
    const readings = [...data.pumpReadings].sort(
      (left, right) =>
        String(left.pumpLabel).localeCompare(String(right.pumpLabel), undefined, {
          numeric: true,
        }) ||
        String(left.nozzle).localeCompare(String(right.nozzle)) ||
        readingRank(left.readingType) - readingRank(right.readingType) ||
        (left.readingSeq ?? 0) - (right.readingSeq ?? 0) ||
        Number(left.readingType.includes('METER_RESET')) -
          Number(right.readingType.includes('METER_RESET')) ||
        pairRank(left.readingType) - pairRank(right.readingType),
    )
    // A change has one row per pump, all with its effectiveFrom; rows come in time order.
    const changeNumbers = new Map<string, number>()
    const changeCountByProduct = new Map<string, number>()
    const priceChanges = data.priceChanges.map((change) => {
      const changeKey = `${change.product}|${change.effectiveFrom}`
      let number = changeNumbers.get(changeKey)
      if (number === undefined) {
        number = (changeCountByProduct.get(change.product) ?? 0) + 1
        changeCountByProduct.set(change.product, number)
        changeNumbers.set(changeKey, number)
      }
      return { change, number }
    })

    return (
      <>
        <SubTitle first>Pump readings</SubTitle>
        <Table
          headers={['Pump', 'Nozzle', 'Product', 'Reading', 'Meter value']}
          emptyMessage='No pump readings were entered.'
          rows={readings.map((reading, index) => ({
            key: `${reading.pumpLabel}-${reading.nozzle}-${reading.readingType}-${reading.readingSeq}-${index}`,
            cells: [
              formatText(reading.pumpLabel),
              formatText(reading.nozzle),
              reading.product,
              readingLabel(reading.readingType, reading.readingSeq),
              formatNumber(reading.value),
            ],
          }))}
        />

        <SubTitle>Tank dips</SubTitle>
        <Table
          headers={['Tank', 'Product', 'Opening dip', 'Closing dip']}
          emptyMessage='No tank dips were entered.'
          rows={data.tankDips.map((dip, index) => ({
            key: `${dip.tankLabel}-${dip.product}-${index}`,
            cells: [
              formatText(dip.tankLabel),
              dip.product,
              formatLitres(dip.openingDipVolume),
              formatLitres(dip.closingDipVolume),
            ],
          }))}
        />

        <SubTitle>Return to tank</SubTitle>
        <Table
          headers={['Product', 'Volume']}
          emptyMessage='No return to tank was entered.'
          rows={data.returnToTank.map((row, index) => ({
            key: `${row.product}-${index}`,
            cells: [row.product, formatLitres(row.volume)],
          }))}
        />

        <SubTitle>Selling prices</SubTitle>
        <Table
          headers={['Product', 'Price per litre']}
          emptyMessage='No selling prices were entered.'
          rows={data.productPrices.map((price, index) => ({
            key: `${price.product}-${index}`,
            cells: [price.product, formatMoney(price.sellingPriceMinor)],
          }))}
        />

        <SubTitle>Price changes</SubTitle>
        <Table
          headers={['Product', 'Change', 'Pump', 'Totalizer reading', 'New price per litre']}
          emptyMessage='No price changes were recorded for this day.'
          rows={priceChanges.map(({ change, number }, index) => ({
            key: change.resourceId || `${change.product}-${number}-${index}`,
            cells: [
              change.product,
              `#${number}`,
              formatText(change.pumpLabel),
              formatNumber(change.totalizerReading),
              formatMoney(change.newPriceMinor),
            ],
          }))}
        />

        <SubTitle>Bank deposits</SubTitle>
        <Table
          headers={['Bank / counterparty', 'Amount', 'Reference']}
          emptyMessage='No bank deposits were entered.'
          rows={data.cashItems.map((item, index) => ({
            key: `${item.counterparty}-${item.reference ?? ''}-${index}`,
            cells: [
              formatText(item.counterparty),
              formatMoney(item.amountMinor),
              formatText(item.reference),
            ],
          }))}
        />
      </>
    )
  }

  const renderAnalysis = (data: FinanceReportAnalysis) => (
    <>
      <SubTitle first>Pump sales against tank sales</SubTitle>
      <Table
        headers={[
          'Product',
          'Pump sales',
          'Tank sales',
          'Gain / loss',
          'Gain / loss % of tank sales',
          'Pump integrity (out of 20)',
          'Gain / loss value',
        ]}
        emptyMessage='No volume analysis is available for this report.'
        rows={data.tolerance.map((row, index) => {
          const gainKnown = isNumber(row.gainLitres)
          return {
            key: `${row.product}-${index}`,
            cells: [
              row.product,
              formatRoundedLitres(row.pumpSalesLitres),
              formatRoundedLitres(row.tankSalesLitres),
              gainKnown ? formatRoundedLitres(row.gainLitres) : 'Not available',
              isNumber(row.gainPctOfTankSales)
                ? `${numberFormatter.format(row.gainPctOfTankSales)}%`
                : 'Not available',
              formatRounded(row.achievedPumpIntegrity),
              formatMoney(row.amountMinor, 'Not available'),
            ],
          }
        })}
      />

      <SubTitle>Computed sales</SubTitle>
      <Table
        headers={[
          'Product',
          'Litres pumped',
          'Return to tank',
          'Net litres',
          'Computed sales',
          'Price changes',
        ]}
        emptyMessage='No computed sales are available for this report.'
        rows={data.sales.map((row, index) => {
          const count = isNumber(row.recordedChangeCount) ? row.recordedChangeCount : 0
          const priceChanges =
            count === 0
              ? 'None recorded'
              : row.priceIntervalsApplied
                ? `${count} applied`
                : isNumber(row.computedSalesMinor)
                  ? `Warning: ${count} recorded but not applied; valued at opening price`
                  : `${count} recorded`
          return {
            key: `${row.product}-${index}`,
            cells: [
              row.product,
              formatRoundedLitres(row.grossLitres),
              formatRoundedLitres(row.rttLitres),
              formatRoundedLitres(row.netLitres),
              formatMoney(row.computedSalesMinor, 'Not available'),
              priceChanges,
            ],
          }
        })}
      />
    </>
  )

  const renderDeliveries = (data: FinanceReportDeliveries) => {
    const tradegrid = data.tradegridDeliveredLitres
    const tradegridEntries = tradegrid ? Object.entries(tradegrid) : []

    return (
      <>
        <Table
          headers={[
            'Product',
            'Invoice / waybill',
            'Invoiced',
            'Received',
            'Shortage',
            'Truck siding',
            'Status',
          ]}
          emptyMessage='No deliveries were logged for this day.'
          rows={data.deliveries.map((delivery, index) => ({
            key: delivery.resourceId || `${delivery.product}-${index}`,
            cells: [
              delivery.product,
              delivery.isPendingInvoiceRef
                ? 'Paperwork pending'
                : formatText(delivery.depotInvoiceRef),
              formatLitres(delivery.invoicedQuantity),
              formatLitres(delivery.receivedQuantity),
              formatLitres(delivery.shortageQuantity),
              formatLitres(delivery.truckSidingVolume),
              DELIVERY_STATUS_LABELS[delivery.status] ?? formatText(delivery.status),
            ],
          }))}
        />

        <SubTitle>TradeGrid completed orders for this day</SubTitle>
        {tradegrid === null ? (
          <p className='text-muted mb-0'>Comparison unavailable.</p>
        ) : tradegridEntries.length === 0 ? (
          <p className='text-muted mb-0'>No completed orders.</p>
        ) : (
          <div className='bx-drd-keyval-list'>
            {tradegridEntries.map(([product, litres]) => (
              <div className='bx-drd-keyval-row' key={product}>
                <span className='bx-drd-keyval-label'>{product}</span>
                <span className='bx-drd-keyval-val'>{formatRoundedLitres(litres)}</span>
              </div>
            ))}
          </div>
        )}
      </>
    )
  }

  const renderCashSales = (data: FinanceCashSalesReconciliation) => (
    <>
      <p className='text-muted'>
        Transaction window: {formatDateTime(data.windowStart)} to {formatDateTime(data.windowEnd)}
      </p>
      <div className='bx-drd-keyval-list'>
        <div className='bx-drd-keyval-row'>
          <span className='bx-drd-keyval-label'>Reported cash sales</span>
          <span className='bx-drd-keyval-val'>
            {formatMoney(data.reportedCashSalesMinor)}
          </span>
        </div>
        <div className='bx-drd-keyval-row'>
          <span className='bx-drd-keyval-label'>Recorded cash sales</span>
          <span className='bx-drd-keyval-val'>{formatMoney(data.recordedCashSalesMinor)}</span>
        </div>
        <div className='bx-drd-keyval-row'>
          <span className='bx-drd-keyval-label'>Anonymous recorded cash sales</span>
          <span className='bx-drd-keyval-val'>{formatMoney(data.anonymousCashSalesMinor)}</span>
        </div>
        <div className='bx-drd-keyval-row'>
          <span className='bx-drd-keyval-label'>Reported minus recorded cash</span>
          <span className='bx-drd-keyval-val'>
            {formatMoney(data.unmatchedCashSalesMinor, 'Not available')}
          </span>
        </div>
      </div>
      <p className='text-muted mt-3 mb-0'>
        Reported minus recorded cash is a transaction-matching difference, not a cash shortage.
      </p>
    </>
  )

  return (
    <>
      <Section title='Report Entries'>
        {renderLoadable(contents, retryContents, renderContents)}
      </Section>
      <Section title='Volume Analysis'>
        {renderLoadable(analysis, retryAnalysis, renderAnalysis)}
      </Section>
      <Section title='Cash Sales Reconciliation'>
        {renderLoadable(cashSales, retryCashSales, renderCashSales)}
      </Section>
      <Section title='Deliveries'>
        {renderLoadable(deliveries, retryDeliveries, renderDeliveries)}
      </Section>
    </>
  )
}

export default DailyReportContentsSections
